import { type ComponentPropsWithoutRef, type ReactNode, useId } from 'react';
import { cn } from '../lib/cn';
import { SectionHeader } from './PageHeader';

/**
 * Sekcja treści (DECISIONS 2026-10-07 „Nowy układ wizualny”, DESIGN §4): biały blok na szarym tle aplikacji —
 * `bg/panel`, zaokrąglenie 12 px, ramka `border/subtle`, padding 24 px góra/dół i 32 px boki (telefon < 768 px: 16 px).
 * Nagłówek sekcji jest wewnątrz bloku: `title` (+ `action`, `tone`) albo własny `SectionHeader` w treści.
 * Listy w środku zachowują linie między wierszami; linii pod ostatnim wierszem nie ma (klasa `fos-section`,
 * reguły w styles/globals.css). Nagłówki stron, okruszki i zakładki zostają na tle — poza sekcją.
 *
 * `padding="none"`: blok bez wewnętrznych odstępów (np. treść, która sama je ustawia, albo tabela od krawędzi).
 */
export function Section({
  title,
  action,
  tone = 'muted',
  padding = 'default',
  className,
  children,
  'aria-labelledby': labelledBy,
  ...rest
}: Omit<ComponentPropsWithoutRef<'section'>, 'title'> & {
  title?: ReactNode;
  action?: ReactNode;
  /** Kolor nagłówka (`SectionHeader`): `strong` — ciemny (profil klienta), `muted` — szary („Wymaga Ciebie”). */
  tone?: 'muted' | 'strong';
  padding?: 'default' | 'none';
}) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={labelledBy ?? (title ? headingId : undefined)}
      className={cn(
        // pierwszy element bez górnego marginesu: odstęp od krawędzi daje padding bloku
        'fos-section min-w-0 rounded-xl border border-line bg-panel [&>:first-child]:mt-0',
        padding === 'default' && 'p-4 md:px-8 md:py-6',
        className,
      )}
      {...rest}
    >
      {title && <SectionHeader title={<span id={headingId}>{title}</span>} action={action} tone={tone} />}
      {children}
    </section>
  );
}
